// domain/model/EligibilityVerification.js
import { VERIFICATION_SOURCE } from '../constants/coverage';

/**
 * EligibilityVerification — what we know about whether a payer relationship is active, and how we
 * came to know it.
 *
 * A VALUE OBJECT: no identity, immutable, equal by value. It describes a state of knowledge about
 * a coverage, which is a descriptive aspect of the domain rather than a thing with a lifetime of
 * its own. Contrast `BillingIntent`, which IS a thing with a lifetime — a decision someone took,
 * which can be released and which accumulates.
 *
 * WHY NOT A BOOLEAN. Verification is about to stop being one thing done one way. Automated checks
 * against the payer (Stedi, 270/271) will write the same fact from a different source, with their
 * own freshness, and manual entry becomes an override alongside them rather than the only option.
 * A bare `verified: true` has nowhere to put either, so adding automation would mean migrating
 * every stored coverage. Recording the source and the moment from the start makes that additive.
 *
 * The second reason stands on its own even without automation: **verification decays**. Member-
 * active is good for something like thirty days, and a boolean cannot express "verified, but
 * stale" because it does not know when it was established. `checkedAt` is what makes that expressible
 * later — nothing reads it today.
 *
 * `checkedAt` IS WHEN THE FACT WAS ESTABLISHED, NOT A PERIOD IT APPLIES TO. It is deliberately not
 * called `asOf`: everywhere else in this context that word means "the date a rule is evaluated
 * against" — `coverageStatus(coverage, asOf)`, `isBillableNow(coverage, asOf)` — and reusing it
 * here would read as the date the verification APPLIES to, which is the same misreading the
 * billing block was reshaped to avoid. Staleness is measured from this instant against NOW; it is
 * not an effective bound, and nothing resolves it against a service date.
 *
 * `checkedAt` / `checkedBy` rather than `verifiedAt` / `verifiedBy` because a `verified: false`
 * fact is still a check that happened. Who VERIFIED it is the wrong question when the answer was
 * no; who CHECKED it is the right one either way.
 *
 * A `verified: false` fact is still a fact once it has a source: a 271 saying "not active" is
 * something we checked and learned, not an absence of knowledge. Today, manual-only, unverified
 * means nobody has looked, so `checkedAt` is null.
 *
 * @domainObject value-object
 * @invariant `verified` and `source` always travel together — a verdict with no provenance cannot
 *   be reasoned about once more than one source exists.
 * @invariant `checkedAt` is an instant, never a calendar date and never a bound. Freshness is measured
 *   from it to now.
 * @invariant Immutable. A new check produces a new fact; nothing edits one in place.
 *
 * @typedef {Object} EligibilityVerification
 * @property {boolean} verified - is the payer relationship active, as far as we know
 * @property {string} source - VERIFICATION_SOURCE; only `manual` today
 * @property {Object|null} checkedAt - server Timestamp: when this fact was established. Null when
 *   nobody has checked. Stamped at the boundary, which owns the clock.
 * @property {string|null} checkedBy - uid, for a manual check. Null for an automated source,
 *   which has no user behind it.
 */

/**
 * Builds a verification fact, filling defaults. A coverage nobody has checked is unverified from
 * a manual source with no timestamp — not "unknown", because manual-only means the absence of a
 * check IS the state.
 *
 * `checkedAt` and `checkedBy` are accepted but normally left null here: the domain owns neither the
 * clock nor the current user, so the adapter stamps them when the fact reaches storage — the same
 * arrangement as `blockedBy` on a billing intent.
 *
 * @param {Partial<EligibilityVerification>} [attrs]
 * @returns {EligibilityVerification} frozen — a value object has no mutators
 */
export function createEligibilityVerification(attrs = {}) {
  return Object.freeze({
    verified: attrs.verified === true,
    source: attrs.source ?? VERIFICATION_SOURCE.MANUAL,
    checkedAt: attrs.checkedAt ?? null,
    checkedBy: attrs.checkedBy ?? null,
  });
} // createEligibilityVerification()

/**
 * Is the payer relationship confirmed active?
 *
 * Deliberately says nothing about freshness. Once an automated source lands, "verified but stale"
 * becomes a distinct answer and belongs here as its own predicate — not folded into this one,
 * where it would silently change what every existing caller means.
 *
 * @param {EligibilityVerification} verification
 * @returns {boolean}
 */
export function isVerified(verification) {
  return verification?.verified === true;
} // isVerified()

/**
 * The fact after someone records a verdict.
 *
 * AN UNCHANGED VERDICT IS NOT A NEW CHECK, and returning the previous fact untouched is what
 * protects the timestamp. Every save re-submits the whole coverage, so building a fresh fact each
 * time would re-date a check nobody repeated — and since `checkedAt` exists to make staleness
 * expressible, that would mean a coverage never went stale as long as somebody kept editing it.
 *
 * A changed verdict IS a new check, so it produces a new, unstamped fact for the boundary to date.
 *
 * @param {EligibilityVerification} previous
 * @param {boolean} verified
 * @returns {EligibilityVerification}
 */
export function applyVerdict(previous, verified) {
  if (previous?.verified === verified) {
    return previous;
  }

  return createEligibilityVerification({ verified });
} // applyVerdict()

/**
 * Has the fact been stamped with when it was established?
 *
 * The adapter uses this to decide whether a write is a NEW check needing a timestamp, or an
 * untouched fact being carried through by an unrelated edit.
 *
 * @param {EligibilityVerification} verification
 * @returns {boolean}
 */
export function isStamped(verification) {
  return verification?.checkedAt !== null && verification?.checkedAt !== undefined;
} // isStamped()
